import Drawer from '@mui/material/Drawer'
import useResponsive from 'src/hooks/useResponsive'
import { drawerWidth, useLayoutContext } from 'src/layouts/layoutContext'

// Colours come from the --left-nav-* tokens defined on :root
// (src/theme/appCssVars.ts), so the drawer looks the same in both appearances
// without branching here.
const DrawerComponent = (props: { children: React.ReactNode }) => {
  const { navState, setNavHandle } = useLayoutContext()
  const { isMobile } = useResponsive()

  const DesktopDrawerProps = {
    onOpen: () => null,
    onClose: () => setNavHandle?.(),
  }

  return (
    <Drawer
      open={isMobile ? navState : true}
      variant={'persistent'}
      anchor="left"
      {...DesktopDrawerProps}
      PaperProps={{
        sx: {
          borderTopRightRadius: { xs: 0, md: 12 },
          borderBottomRightRadius: { xs: 0, md: 12 },

          width: isMobile ? '100%' : drawerWidth,
          height: '100dvh',
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
          isolation: 'isolate',
          boxSizing: 'border-box',
          transition:
            'width 200ms, background-color 180ms ease, box-shadow 180ms ease, border-color 180ms ease',

          // The full-width mobile drawer covers the page, so it gets an opaque
          // base instead of a translucent one (and no expensive blur).
          backgroundColor: isMobile
            ? 'var(--app-page-bg)'
            : 'var(--left-nav-surface)',
          backgroundImage: 'var(--left-nav-surface-gradient)',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          borderRight: '1px solid var(--left-nav-border)',
          boxShadow: 'var(--left-nav-shadow)',
          overflow: 'hidden',

          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: 0,
            pointerEvents: 'none',
            borderRadius: 'inherit',
            background: 'var(--left-nav-sheen)',
            boxShadow: 'var(--left-nav-sheen-shadow)',
            transition: 'opacity 180ms ease',
            zIndex: 0,
          },
          '& > *': {
            position: 'relative',
            zIndex: 1,
          },

          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : drawerWidth,
            boxSizing: 'border-box',
          },

          '.padding': {
            ml: -3,
            transition: '0.15s',
          },
          '.opacity': {
            opacity: { xs: 1, md: 0 },
            transition: '0.15s',
          },
          '.railLabel': {
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
            transition: 'opacity 160ms ease, transform 160ms ease',
          },

          '.disappear': {
            display: isMobile ? 'flex' : 'none',
          },

          '.height': {
            transition: '0.15s',
          },
          '.nav-list-title': {
            color: 'var(--left-nav-section-color)',
          },
          '.nav-section-title .MuiTypography-root, .nav-section-title svg': {
            color: 'var(--left-nav-section-color)',
          },

          pb: 0,

          ...(isMobile
            ? {}
            : {
                '&:hover': {
                  width: 255,
                  backgroundColor: 'var(--left-nav-expanded-surface)',
                  backgroundImage: 'var(--left-nav-expanded-gradient)',
                  backdropFilter: 'var(--left-nav-expanded-blur)',
                  WebkitBackdropFilter: 'var(--left-nav-expanded-blur)',
                  borderRight: '1px solid var(--left-nav-expanded-border)',
                  boxShadow: 'var(--left-nav-expanded-shadow)',
                  '&::before': {
                    opacity: 0.22,
                  },

                  '.railLabel': {
                    opacity: 0,
                    transform: 'translateX(-50%) translateY(-4px)',
                  },

                  '.disappear': {
                    display: 'flex',
                  },

                  '.height': {
                    height: 'auto !important',
                  },

                  '.opacity': {
                    opacity: 1,
                  },

                  '.padding': {
                    ml: 0,
                  },
                },
              }),
        },
      }}
    >
      {props.children}
    </Drawer>
  )
}

export default DrawerComponent
